import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import QuestionForm, {
  type QuestionFormSubmitPayload,
  type QuestionFormValues,
} from './QuestionForm';
import { createQuestion } from '../services/questions';
import { ApiError } from '@admin/services/api';
import { useToast } from '@admin/stores/toastStore';
import AssistPanel from './AssistPanel';
import { Difficulty, QuestionType } from '@games/word-clash/shared/types';

const EMPTY_VALUES: QuestionFormValues = {
  type: QuestionType.Category,
  difficulty: Difficulty.Medium,
  category: 'general',
  promptEn: '',
  promptTr: '',
  validAnswers: [],
  validAnswersTr: [],
  specialAnswers: [],
  specialAnswersTr: [],
  plausibleWrongAnswers: [],
  plausibleWrongAnswersTr: [],
  exampleHint: '',
  exampleHintTr: '',
  aliases: [],
  aliasesTr: [],
  contentFlags: [],
};

/**
 * New-question route. Delegates to the shared <QuestionForm />. On success
 * we toast and bounce to the list screen. Server validation errors surface
 * inline on the form.
 */
export default function QuestionNew() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  async function handleSubmit(payload: QuestionFormSubmitPayload) {
    setSubmitting(true);
    setServerError(null);
    try {
      await createQuestion({
        type: payload.type,
        difficulty: payload.difficulty,
        category: payload.category,
        promptEn: payload.promptEn,
        promptTr: payload.promptTr,
        validAnswers: payload.validAnswers,
        validAnswersTr: payload.validAnswersTr,
        specialAnswers: payload.specialAnswers,
        specialAnswersTr: payload.specialAnswersTr,
        plausibleWrongAnswers: payload.plausibleWrongAnswers,
        plausibleWrongAnswersTr: payload.plausibleWrongAnswersTr,
        exampleHint: payload.exampleHint,
        exampleHintTr: payload.exampleHintTr,
        answerAliases: payload.answerAliases,
        answerAliasesTr: payload.answerAliasesTr,
        contentFlags: payload.contentFlags,
      });
      showToast('Question created', 'success');
      navigate('/questions');
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${err.error}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Failed to create question';
      setServerError(message);
      showToast('Failed to create question', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4" data-testid="questions-new-screen">
      <div className="flex items-center gap-3">
        <Link
          to="/questions"
          className="text-sm text-slate-600 hover:text-slate-900"
          data-testid="questions-new-back"
        >
          ← Back to Questions
        </Link>
      </div>
      <h1 className="text-lg font-semibold text-slate-900">New question</h1>
      <QuestionForm
        initial={EMPTY_VALUES}
        onSubmit={handleSubmit}
        submitLabel="Create question"
        submitting={submitting}
        serverError={serverError}
        renderAssistant={(ctx) => <AssistPanel {...ctx} />}
      />
    </div>
  );
}
